import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  orderKeyAfterLast,
  base64ToBytes,
  unfurlResponseSchema,
  wishContentSchema,
  type ContentBlob,
  type UnfurlRequest,
  type UnfurlResult,
  type WishContent,
  type WishStatus,
} from "@vmeste/shared";
import { t } from "../i18n/index.js";
import { operations } from "../lib/outbox.js";
import { wishPositions } from "../lib/store.js";
import { syncEngine } from "../lib/sync.js";
import { api } from "../lib/api.js";
import { sealForUpload } from "../lib/media.js";
import { toWishViews } from "../lib/views.js";
import type { WishView } from "./views.js";
import { plaintextOf } from "../lib/plaintext.js";

/**
 * Wishes (SPEC §4.3).
 *
 * A section of its own, not a third kind of list — the first draft of the spec
 * had it both ways, and the same thing showed up twice on screen.
 *
 * Like lists, everything here goes through the outbox and renders immediately.
 * The link preview is the one thing that cannot: only the server can read
 * somebody else's page (CORS), so the wish is created first and the preview
 * folded into its content whenever it turns up — or never, which is a normal
 * outcome and not an error state (SPEC §4.3: Instagram, TikTok and Pinterest
 * block scrapers, and we degrade to "link + your own words" rather than fight).
 */

/*
 * The codec comes from the space every write is going into (SPEC §3). `null`
 * means the space is encrypted and this device has no key, and then nothing is
 * written at all: plaintext in a space that is supposed to be encrypted is a
 * far worse outcome than a refused write.
 */

/** The unfurl gives up after this, so "загружаем" never hangs forever. */
const UNFURL_TIMEOUT_MS = 12_000;

export interface WishPreview {
  title?: string;
  description?: string;
  /** A path on our own origin (`/api/media/…`), never the shop's address. */
  image?: string;
  siteName?: string;
  fetchedAt?: string;
}

/**
 * A wish as this screen renders it.
 *
 * `title` is deliberately the person's own, not the unfurled one: they typed it
 * on purpose, and "плед на диван" beats a marketplace's 200-character headline.
 * The preview's title is offered next to it when the two differ.
 */
export interface WishItemView extends WishView {
  preview?: WishPreview;
  /** A preview is being fetched for this one wish right now. */
  unfurling: boolean;
}

export interface NewWish {
  title: string;
  url?: string;
  note?: string;
  price?: string;
}

export interface WishActions {
  wishes: WishItemView[];
  add(input: NewWish): void;
  setStatus(wishId: string, status: WishStatus): void;
  toggleClaim(wishId: string): void;
  remove(wishId: string): void;
  /** Ask the server to read the page again — for one that came back empty. */
  refreshPreview(wishId: string): void;
}

/* --------------------------------------------------------- in-flight store */

/**
 * Which wishes are waiting on an unfurl.
 *
 * Module-level rather than component state on purpose: switching tabs unmounts
 * this screen while the request is still out, and the fold must still land when
 * the person comes back — as must the "already asking" guard, so returning to
 * the screen and pressing the button again does not start a second fetch.
 */
const unfurling = new Set<string>();
const unfurlListeners = new Set<() => void>();
let unfurlVersion = 0;

function subscribeUnfurling(listener: () => void): () => void {
  unfurlListeners.add(listener);
  return () => unfurlListeners.delete(listener);
}

function unfurlingSnapshot(): number {
  return unfurlVersion;
}

function setUnfurling(wishId: string, running: boolean): void {
  if (running === unfurling.has(wishId)) return;
  if (running) unfurling.add(wishId);
  else unfurling.delete(wishId);

  unfurlVersion += 1;
  for (const listener of unfurlListeners) listener();
}

/**
 * Resolves once nothing is waiting on an unfurl.
 *
 * Only `releasePreviewImage` wants this, and for a narrow reason: a preview on
 * its way in has not been written to any wish yet, so a scan taken while one is
 * in flight can truthfully find nobody showing a picture that is about to be
 * shown. Which sounds harmless until the picture arriving is the very one being
 * given up — a link changed to another page of the same shop, serving the same
 * photograph, which is the same bytes and therefore the same file. Waiting is
 * enough because `foldPreview` writes the wish before it clears the flag.
 */
function whenNothingUnfurling(): Promise<void> {
  if (unfurling.size === 0) return Promise.resolve();

  return new Promise((resolve) => {
    const stop = subscribeUnfurling(() => {
      if (unfurling.size > 0) return;
      stop();
      resolve();
    });
  });
}

/* ---------------------------------------------------------------- decoding */

/**
 * Decode a wish's content for rendering.
 *
 * Same shape as the sync layer's decoder and for the same reason: `none` mode
 * carries plain JSON, so no await is needed and rendering stays synchronous. An
 * e2ee blob cannot be read this way, and when that mode arrives the decode moves
 * to where a delta is applied — components still never see a ContentBlob.
 */
function readWishContent(blob: ContentBlob | undefined): WishContent | null {
  const value = plaintextOf(blob);
  if (value === undefined) return null;
  const parsed = wishContentSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/* ------------------------------------------------ giving a picture back */

/**
 * The path a preview picture is stored under, exactly as the server minted it.
 *
 * A path that is not one of ours is not something to ask the server to delete,
 * so anything else reads as "no picture here" rather than as an id to pass on.
 */
const MEDIA_PATH = /^\/api\/media\/([0-9a-f]{64}\.[a-z0-9]{1,8})$/;

/** The picture a wish is currently showing, if it has one this device can read. */
function previewImageOf(spaceId: string, wishId: string): string | undefined {
  const wish = syncEngine.view(spaceId).wishes[wishId];
  return readWishContent(wish?.content)?.preview?.image;
}

/**
 * Let go of a preview picture no wish in this space is showing any more.
 *
 * The server cannot work this out and says so where a wish is deleted: the path
 * lives inside the wish's content blob, which is ciphertext in an encrypted
 * space and which SPEC §3 forbids reading in any other. This side holds the
 * decrypted wishes and can see every preview at once, so the question "is
 * anybody still showing this?" is ours to answer and this is the answer being
 * sent.
 *
 * **Only when nothing else is showing it.** A file is named by the salted hash
 * of its bytes, so the same photograph unfurled onto two wishes is one file on
 * the disk, and letting it go for one of them would blank the other. Hence the
 * scan across the whole space rather than a look at the wish in hand — and a
 * wish this device cannot decode counts as "maybe", because litter on a disk is
 * a far better outcome than a picture that goes blank somewhere else.
 *
 * `after` is the change that abandoned the picture. Waiting for it to reach the
 * server is what keeps an abandoned outbox entry honest: a delete that never
 * lands leaves the wish where it was, and it would come back pointing at a file
 * this call had already removed. The wait is also the moment the state is
 * freshest, since anything the other device sent has had its best chance to
 * arrive by then.
 *
 * Failure is deliberately silent. Whatever referred to the picture is already
 * gone; an unreleased file is litter, not lost work, and it is exactly what the
 * whole app left behind until this route existed.
 */
export async function releasePreviewImage(
  spaceId: string,
  image: string | undefined,
  { after }: { after: string },
): Promise<void> {
  const mediaId = image?.match(MEDIA_PATH)?.[1];
  if (!mediaId) return;

  if ((await syncEngine.whenSettled(after)) !== "sent") return;
  await whenNothingUnfurling();

  for (const wish of Object.values(syncEngine.view(spaceId).wishes)) {
    const content = readWishContent(wish.content);
    if (!content || content.preview?.image === image) return;
  }

  try {
    await api.releaseMedia(spaceId, mediaId);
  } catch {
    // Nothing to tell anybody and nothing to retry — see above.
  }
}

/* ------------------------------------------------------------------ unfurl */

/**
 * The image must live on our origin.
 *
 * The endpoint's contract says it caches the picture and hands back a path, and
 * the wish card renders it straight into an `<img src>`. If anything absolute
 * ever came back, rendering it would hotlink the shop — telling them every time
 * either person opened their own wish list. Cheaper to refuse it here than to
 * trust the other end forever.
 */
function sameOriginPath(image: string | undefined): string | undefined {
  if (!image) return undefined;
  return image.startsWith("/") && !image.startsWith("//") ? image : undefined;
}

/**
 * Seal the picture the server handed back, and store it.
 *
 * Only ever reached in an `e2ee` space, because only there does the unfurl
 * response carry bytes instead of a path: the server has to fetch the picture —
 * CORS is why unfurl is on the server at all — but it has no key, so keeping it
 * would leave the one readable thing in a wish that is otherwise ciphertext.
 *
 * Failure is `null` and the wish keeps everything else. A preview without a
 * picture is still a preview, and it is a far better outcome than no preview or
 * a picture stored in the clear.
 */
async function sealPreviewImage(
  spaceId: string,
  result: UnfurlResult,
): Promise<string | undefined> {
  if (!result.inlineImage) return undefined;

  try {
    const raw = base64ToBytes(result.inlineImage.data);
    const sealed = await sealForUpload(
      spaceId,
      new File([raw as BufferSource], "preview", { type: result.inlineImage.mime }),
    );

    const { url } = await api.storeSealedMedia(spaceId, sealed);
    return sameOriginPath(url);
  } catch {
    return undefined;
  }
}

/** True when the page gave up something worth saving. */
function isUseful(result: UnfurlResult): boolean {
  return Boolean(
    result.title ||
      result.description ||
      result.siteName ||
      result.price ||
      sameOriginPath(result.image) ||
      // The encrypted space's picture arrives as bytes rather than a path, and
      // a page that offered nothing but a photograph still offered something.
      result.inlineImage,
  );
}

/**
 * POST the URL to the space's unfurl endpoint.
 *
 * Deliberately not on `api` in `src/lib`: that client is somebody else's file
 * and the endpoint is still being written. Everything that can go wrong — no
 * network, a 502, a body that does not match the schema, a site that took too
 * long — collapses to `null` here, because from the screen's point of view they
 * are the same outcome: the wish keeps its link and gets no preview.
 */
async function fetchPreview(spaceId: string, url: string): Promise<UnfurlResult | null> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), UNFURL_TIMEOUT_MS);

  try {
    const body: UnfurlRequest = { url };
    const response = await fetch(
      `/api/spaces/${encodeURIComponent(spaceId)}/unfurl`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
        signal: abort.signal,
      },
    );

    if (!response.ok) return null;

    const parsed = unfurlResponseSchema.safeParse(await response.json());
    return parsed.success ? parsed.data.preview : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Fetch a preview and fold it into the wish's content.
 *
 * Inside the blob rather than beside it, so an e2ee space keeps its previews:
 * the server read the page but cannot see what was saved about it.
 */
async function foldPreview(spaceId: string, wishId: string): Promise<void> {
  if (unfurling.has(wishId)) return;

  // Asked for before the request goes out, not after it comes back: a locked
  // space has nowhere to put the preview, so there is no point fetching it.
  const codec = syncEngine.codec(spaceId);
  if (!codec) return;

  const before = readWishContent(syncEngine.view(spaceId).wishes[wishId]?.content);
  const url = before?.url;
  if (!url) return;

  setUnfurling(wishId, true);
  try {
    const result = await fetchPreview(spaceId, url);
    if (!result || !isUseful(result)) return;

    // Re-read: the person kept using the app while the request was out, and the
    // wish may have been renamed, priced — or deleted, in which case there is
    // nothing left to update.
    const wish = syncEngine.view(spaceId).wishes[wishId];
    const current = readWishContent(wish?.content);
    if (!wish || !current) return;

    // In an encrypted space the server hands the picture back rather than
    // keeping it, so sealing and storing it is this device's job. It happens
    // before the blob is written: a preview naming a file that never landed
    // would show a broken picture for ever.
    const image = (await sealPreviewImage(spaceId, result)) ?? sameOriginPath(result.image);

    const preview: WishPreview = {
      ...(result.title ? { title: result.title } : {}),
      ...(result.description ? { description: result.description } : {}),
      ...(image ? { image } : {}),
      ...(result.siteName ? { siteName: result.siteName } : {}),
      fetchedAt: new Date().toISOString(),
    };

    const next: WishContent = {
      ...current,
      preview,
      // A parsed price fills an empty field and never overwrites a typed one:
      // what the person wrote about their own wish outranks what a shop says.
      ...(!current.price && result.price ? { price: result.price } : {}),
    };

    const content = await codec.encode(next);
    const operation = operations.updateWish(spaceId, wishId, { content });
    syncEngine.mutate(operation);

    // The picture this one replaces is now shown by nothing — unless another
    // wish saved the same one, which is `releasePreviewImage`'s to check. A
    // refresh in an encrypted space always lands here: every seal draws a fresh
    // iv, so the same photograph sealed twice is two files.
    if (current.preview?.image && current.preview.image !== image) {
      void releasePreviewImage(spaceId, current.preview.image, { after: operation.id });
    }
  } finally {
    setUnfurling(wishId, false);
  }
}

/* -------------------------------------------------------------------- hook */

/** A Web Push payload caps at 4KB after encryption, and base64 inflates a third. */
const PREVIEW_BODY_MAX = 240;

const clampPreview = (text: string): string =>
  text.length <= PREVIEW_BODY_MAX ? text : `${text.slice(0, PREVIEW_BODY_MAX - 1)}…`;

/**
 * What the other person will see on their lock screen, which is a name and not
 * an id. Read from the members already synced, exactly as the chat does it.
 */
function nameOf(spaceId: string, self: string | null): string {
  const name = syncEngine
    .view(spaceId)
    .members.find((member) => member.userId === self)?.user.displayName;

  return name ?? t("chat.someone");
}

export function useWishes(spaceId: string | null, self: string | null): WishActions {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);
  const unfurls = useSyncExternalStore(subscribeUnfurling, unfurlingSnapshot);

  const wishes = useMemo(() => {
    if (!spaceId) return [];

    const state = syncEngine.view(spaceId);

    return toWishViews(state).map((view): WishItemView => {
      const content = readWishContent(state.wishes[view.id]?.content);

      return {
        ...view,
        // The sync layer's view prefers the unfurled title; here the person's
        // own wins, and the other one is shown beside it if it says anything
        // different.
        ...(content?.title ? { title: content.title } : {}),
        ...(content?.preview ? { preview: content.preview } : {}),
        unfurling: unfurling.has(view.id),
      };
    });
  }, [spaceId, snapshot, unfurls]);

  const add = useCallback(
    (input: NewWish) => {
      if (!spaceId) return;

      const title = input.title.trim();
      if (!title) return;

      // Read before the encodes below rather than inside them: the person can
      // switch spaces while a promise is out, and this one is the space the
      // wish was typed into.
      const codec = syncEngine.codec(spaceId);
      if (!codec) return;

      const url = input.url?.trim();
      const note = input.note?.trim();
      const price = input.price?.trim();

      /*
       * The notification's words are composed here, next to the wish itself,
       * and go through the same codec — so in an encrypted space the server
       * forwards a blob it cannot read and the other device decrypts it
       * (SPEC §3, decision 3). Composing it server-side would mean either
       * plaintext the server can read or a useless "новая хотелка".
       *
       * `notifySpace` skips the person who acted, so nobody is told about their
       * own wish.
       */
      void Promise.all([
        codec.encode({
          title,
          ...(url ? { url } : {}),
          ...(note ? { note } : {}),
          ...(price ? { price } : {}),
        }),
        /*
         * Who, and what — the same shape the chat's preview has.
         *
         * It used to be «Новая хотелка: Наушники» and nothing else, which on a
         * lock screen is almost word for word the neutral line shown when a
         * device *cannot* read the push at all. So a working decryption looked
         * exactly like a failed one, and the answer to «что там» was still
         * "open the app".
         *
         * The person goes in the title because that is the first thing anybody
         * wants from a shared space, and the price and the note go in the body
         * where there is room for them. «Имя: …» rather than a sentence with a
         * verb in it — Russian would need to know whether that verb is
         * «добавил» or «добавила», and a name does not say.
         */
        codec.encode({
          title: t("wishes.notifyAdded", { name: nameOf(spaceId, self) }),
          body: clampPreview(
            [title, price, note].filter((part) => part && part.length > 0).join(" · "),
          ),
        }),
      ]).then(([content, preview]) => {
          // After the encode, not before: see the note in `addItem`.
          const position = orderKeyAfterLast(wishPositions(syncEngine.view(spaceId)));

          const operation = operations.createWish(
            spaceId,
            { content, position, status: "idea" },
            { type: "wish.added", targetType: "wish", preview },
          );
          syncEngine.mutate(operation);

          // The wish is already on screen. The preview arrives later or not at
          // all; either way nobody waits for a shop's server to answer.
          if (url) void foldPreview(spaceId, operation.body.id ?? operation.id);
        });
    },
    [spaceId],
  );

  const setStatus = useCallback(
    (wishId: string, status: WishStatus) => {
      if (!spaceId) return;
      syncEngine.mutate(operations.updateWish(spaceId, wishId, { status }));
    },
    [spaceId],
  );

  const toggleClaim = useCallback(
    (wishId: string) => {
      if (!spaceId) return;
      const wish = syncEngine.view(spaceId).wishes[wishId];
      if (!wish) return;

      // `claimedBy` is the server's to set — sending it would be this client
      // claiming on somebody else's behalf.
      syncEngine.mutate(
        operations.updateWish(spaceId, wishId, { claimed: wish.claimedBy !== self }),
      );
    },
    [spaceId, self],
  );

  const remove = useCallback(
    (wishId: string) => {
      if (!spaceId) return;

      // Read before the delete: the path is inside the wish's content, and a
      // line from now the wish is out of the view for good.
      const image = previewImageOf(spaceId, wishId);

      const operation = operations.deleteWish(spaceId, wishId);
      syncEngine.mutate(operation);

      // The server keeps the picture whatever happens to the wish — it cannot
      // read the blob that names it (SPEC §3) — so this is the only side that
      // can hand it back.
      void releasePreviewImage(spaceId, image, { after: operation.id });
    },
    [spaceId],
  );

  const refreshPreview = useCallback(
    (wishId: string) => {
      if (!spaceId) return;
      void foldPreview(spaceId, wishId);
    },
    [spaceId],
  );

  return { wishes, add, setStatus, toggleClaim, remove, refreshPreview };
}
